import type { DocumentSource, DocumentType } from '@core/domain/model';

/** Вид документа определяет значок строки — порт `DocumentKind`. */
export type DocumentKind = 'pdf' | 'photo' | 'other';

/**
 * Порядок документов в деле.
 *
 * По умолчанию `byType`: дело сразу открывается разделами — иск, доверенности, акты
 * суда. Решение владельца от 30.08.2026: в деле на полсотни документов структура нужнее
 * ручного порядка. `manual` остаётся для дел, где юрист выстраивает оглавление сам.
 */
export type DocumentsSort = 'manual' | 'byType' | 'newest';

export const SORTS: { key: DocumentsSort; label: string }[] = [
  { key: 'byType', label: 'По типу' },
  { key: 'newest', label: 'Сначала новые' },
  { key: 'manual', label: 'Свой порядок' },
];

/** Быстрый отбор над списком: «покажи только определения суда». */
export type DocumentsFilter = { kind: 'all' } | { kind: 'favorites' } | { kind: 'type'; type: DocumentType };

/** Строка библиотеки: готовый к показу текст, без форматирования в вёрстке. */
export interface DocumentListItem {
  id: string;
  title: string;
  /** «PDF · 9 стр. · 1,2 МБ · 15.08.2026» — без типа, он показывается отдельно. */
  meta: string;
  isFavorite: boolean;
  kind: DocumentKind;
  /** Откуда файл: от этого зависит предупреждение при объединении (шаг 12). */
  source: DocumentSource;
  type: DocumentType;
  /** «Иск». Пусто, если тип не задан: отсутствие сведений не показываем. */
  typeLabel: string;
}

/**
 * Элемент списка: заголовок группы либо документ.
 *
 * Заголовки появляются только при сортировке по типу — тогда список читается
 * как оглавление с разделами, и раскладывать документы по папкам руками не нужно.
 */
export type DocumentsListEntry =
  | { kind: 'header'; title: string; count: number }
  | { kind: 'item'; document: DocumentListItem; canDrag: boolean };

/**
 * Отменяемое действие: удаление, а позже и объединение (шаг 12).
 *
 * Объединение будет жить здесь же намеренно: с точки зрения юриста это то же самое —
 * документы пропали из списка, и «Отменить» обязано их вернуть.
 */
export interface PendingDeletion {
  ids: string[];
  message: string;
  /** Документ, созданный объединением: при отмене он уходит вместе с возвратом частей. */
  createdId?: string | null;
}

export interface DocumentsState {
  isLoading: boolean;
  caseTitle: string;
  /** «12 документов · 84 МБ» */
  summary: string;
  entries: DocumentsListEntry[];
  sort: DocumentsSort;
  filter: DocumentsFilter;
  /** Типы, которые в этом деле есть: фильтровать по отсутствующим незачем. */
  availableTypes: DocumentType[];
  hasFavorites: boolean;
  selectedIds: string[];
  /**
   * Режим перестановки: строки тянутся целиком, вверху «Готово».
   *
   * Включается выбором «Своего порядка», а не отдельным пунктом: на Android
   * перетаскивание жило молча при этой сортировке и ручкой в четыре миллиметра
   * справа — партнёр не нашла ни того, ни другого (обратная связь № 1, пункт 8).
   */
  isReordering: boolean;
  pendingDeletion: PendingDeletion | null;
  isImporting: boolean;
  errorMessage: string | null;
}

/** Документы, видимые сейчас, — без заголовков групп. */
export const visibleDocuments = (entries: DocumentsListEntry[]): DocumentListItem[] =>
  entries.filter((entry) => entry.kind === 'item').map((entry) => entry.document);
